// components/GuestManager.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// confirmed values: 'accepted' | 'declined' | 'maybe' | null (no response)
const CONFIRMATION_OPTIONS = [
  { value: 'accepted',  label: '✅', badgeClass: 'bg-success',  textClass: 'text-success'  },
  { value: 'maybe',    label: '❓', badgeClass: 'bg-warning',  textClass: 'text-warning'  },
  { value: 'declined', label: '❌', badgeClass: 'bg-danger',   textClass: 'text-danger'   },
  { value: null,       label: '⏳', badgeClass: 'bg-secondary', textClass: 'text-muted'   },
];

function confirmationBadge(value, t) {
  const opt = CONFIRMATION_OPTIONS.find(o => o.value === value) || CONFIRMATION_OPTIONS[3];
  const labelKey = value === 'accepted' ? 'guests.accepted'
                 : value === 'maybe'    ? 'guests.maybe'
                 : value === 'declined' ? 'guests.declined'
                 : 'guests.no_response';
  return (
    <span className={`badge ${opt.badgeClass} text-white`} style={{ fontSize: '0.75rem' }}>
      {opt.label} {t(labelKey)}
    </span>
  );
}

const toTitleCase = (str) => str.replace(/(^|\s)\S/g, c => c.toUpperCase());

export default function GuestManager({ user, currentEvent, onEventUpdate }) {
  const { t } = useTranslation();
  const [guests, setGuests] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [newGuest, setNewGuest] = useState({
    name: '',
    invited: false,
    confirmed: null,   // null | 'accepted' | 'maybe' | 'declined'
    adults: 1,
    children: 0,
    giftValue: ''
  });

  const canEdit = () => {
    if (!currentEvent) return false;
    if (!currentEvent.isShared) return true;
    return currentEvent.userRole === 'operator';
  };

  useEffect(() => {
    if (currentEvent) {
      // Migrate legacy boolean confirmed → 'accepted' | null
      const migrated = (currentEvent.guests || []).map(g => ({
        ...g,
        confirmed: g.confirmed === true ? 'accepted'
                 : g.confirmed === false || g.confirmed === null || g.confirmed === undefined ? null
                 : g.confirmed   // already a string value
      }));
      setGuests(migrated);
    }
  }, [currentEvent]);

  const saveToFirebase = async (updatedGuests) => {
    if (!user || !currentEvent) return;
    setSaving(true);
    try {
      const ownerId = currentEvent.ownerId || user.uid;
      const eventRef = doc(db, 'users', ownerId, 'events', currentEvent.id);
      const updatedGifts = updatedGuests
        .filter(g => g.giftValue && g.giftValue > 0)
        .map(g => ({
          id: g.id,
          guest: g.name,
          amount: parseFloat(g.giftValue),
          adults: g.adults || 1,
          children: g.children || 0
        }));
      await setDoc(eventRef, { guests: updatedGuests, gifts: updatedGifts }, { merge: true });
      setGuests(updatedGuests);
      onEventUpdate({ ...currentEvent, guests: updatedGuests, gifts: updatedGifts });
    } catch (error) {
      console.error('Error saving:', error);
      alert(`${t('guests.save_error')}\n\n${error.code || ''} ${error.message || error}`);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (guest) => {
    setEditingId(guest.id);
    setEditData({
      name: guest.name,
      invited: guest.invited,
      confirmed: guest.confirmed ?? null,
      adults: guest.adults || 1,
      children: guest.children || 0,
      giftValue: guest.giftValue || ''
    });
  };

  const saveEdit = async () => {
    const updatedGuests = guests.map(g =>
      g.id === editingId
        ? {
            ...g,
            name: editData.name,
            invited: editData.invited,
            confirmed: editData.confirmed,
            adults: parseInt(editData.adults) || 1,
            children: parseInt(editData.children) || 0,
            giftValue: editData.giftValue ? parseFloat(editData.giftValue) : null
          }
        : g
    );
    await saveToFirebase(updatedGuests);
    setEditingId(null);
    setEditData({});
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const addGuest = async () => {
    if (!newGuest.name.trim()) {
      setNameError(true);
      return;
    }
    const guestToAdd = {
      id: Date.now(),
      name: newGuest.name,
      invited: newGuest.invited,
      confirmed: newGuest.confirmed,
      adults: parseInt(newGuest.adults) || 1,
      children: parseInt(newGuest.children) || 0,
      giftValue: newGuest.giftValue ? parseFloat(newGuest.giftValue) : null
    };
    await saveToFirebase([guestToAdd, ...guests]);
    setNewGuest({ name: '', invited: false, confirmed: null, adults: 1, children: 0, giftValue: '' });
  };

  const deleteGuest = async (id) => {
    setGuestToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteGuest = async () => {
    if (guestToDelete) {
      await saveToFirebase(guests.filter(g => g.id !== guestToDelete));
      setShowDeleteConfirm(false);
      setGuestToDelete(null);
    }
  };

  const toggleInvited = async (id, currentStatus) => {
    const updatedGuests = guests.map(g =>
      g.id === id ? { ...g, invited: !currentStatus } : g
    );
    await saveToFirebase(updatedGuests);
  };

  const setConfirmation = async (id, value) => {
    const updatedGuests = guests.map(g =>
      g.id === id ? { ...g, confirmed: value || null } : g
    );
    await saveToFirebase(updatedGuests);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return <i className="fas fa-sort ms-1 text-muted opacity-50" style={{ fontSize: '0.7rem' }}></i>;
    return sortDir === 'asc'
      ? <i className="fas fa-sort-up ms-1" style={{ fontSize: '0.7rem' }}></i>
      : <i className="fas fa-sort-down ms-1" style={{ fontSize: '0.7rem' }}></i>;
  };

  // Statistics
  const totalGuests = guests.length;
  const totalAdults = guests.reduce((sum, g) => sum + (g.adults || 1), 0);
  const totalChildren = guests.reduce((sum, g) => sum + (g.children || 0), 0);
  const totalPersons = totalAdults + totalChildren;
  const totalGifts = guests.reduce((sum, g) => sum + (g.giftValue || 0), 0);
  const invitedCount = guests.filter(g => g.invited).length;
  const acceptedGuests  = guests.filter(g => g.confirmed === 'accepted');
  const maybeGuests     = guests.filter(g => g.confirmed === 'maybe');
  const declinedGuests  = guests.filter(g => g.confirmed === 'declined');
  const noResponseGuests = guests.filter(g => g.invited && !g.confirmed);
  const acceptedCount = acceptedGuests.length;
  const maybeCount    = maybeGuests.length;
  const declinedCount = declinedGuests.length;
  const noResponseCount = noResponseGuests.length;
  const personCount = (arr) => ({
    adults: arr.reduce((s, g) => s + (g.adults || 1), 0),
    children: arr.reduce((s, g) => s + (g.children || 0), 0),
    total: arr.reduce((s, g) => s + (g.adults || 1) + (g.children || 0), 0),
  });
  const acceptedPersons  = personCount(acceptedGuests);
  const maybePersons     = personCount(maybeGuests);
  const declinedPersons  = personCount(declinedGuests);
  const noResponsePersons = personCount(noResponseGuests);
  const giftCount = guests.filter(g => g.giftValue && g.giftValue > 0).length;

  const searched = guests.filter(g => g.name.toLowerCase().includes(searchText.toLowerCase()));
  const filteredGuests = sortField
    ? [...searched].sort((a, b) => {
        let va = a[sortField], vb = b[sortField];
        if (sortField === 'confirmed') { const order = { accepted: 0, maybe: 1, null: 2, declined: 3 }; va = order[va] ?? 2; vb = order[vb] ?? 2; }
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va == null) va = sortDir === 'asc' ? Infinity : -Infinity;
        if (vb == null) vb = sortDir === 'asc' ? Infinity : -Infinity;
        return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
      })
    : searched;

  if (!currentEvent) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center py-5">
          <div className="fs-1">⚠️</div>
          <h4>{t('guests.no_event_selected')}</h4>
          <p>{t('guests.select_event_instruction')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white fw-bold text-success">
        🎁 {t('guests.title')} - {currentEvent.name}
        {saving && <span className="badge bg-secondary ms-2">{t('guests.saving')}</span>}
      </div>
      <div className="card-body">
        {/* Add new guest form */}
        <div className="row g-2 mb-4 p-3 bg-light rounded align-items-end">
          <div className="col-12 col-md-3">
            <label className="form-label small text-muted mb-1">👤 {t('guests.name')}</label>
            <input
              className={`form-control${nameError ? ' is-invalid' : ''}`}
              value={newGuest.name}
              onChange={e => { setNewGuest({...newGuest, name: toTitleCase(e.target.value)}); if (nameError) setNameError(false); }}
              onKeyPress={e => e.key === 'Enter' && addGuest()}
              disabled={!canEdit()}
            />
          </div>

          <div className="col-6 col-md-1">
            <label className="form-label small text-muted mb-1 d-block">📨 {t('guests.invited_short')}</label>
            <div
              className="form-control d-flex align-items-center justify-content-center"
              style={{ cursor: canEdit() ? 'pointer' : 'default' }}
              onClick={() => canEdit() && setNewGuest({...newGuest, invited: !newGuest.invited})}
            >
              <input
                className="form-check-input"
                type="checkbox"
                id="newInvited"
                checked={newGuest.invited}
                onChange={e => setNewGuest({...newGuest, invited: e.target.checked})}
                disabled={!canEdit()}
                style={{ width: '1.2em', height: '1.2em', cursor: canEdit() ? 'pointer' : 'not-allowed' }}
                onClick={e => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="col-6 col-md-2">
            <label className="form-label small text-muted mb-1">✉️ {t('guests.confirmed_short')}</label>
            <select
              className="form-select"
              value={newGuest.confirmed ?? ''}
              onChange={e => setNewGuest({...newGuest, confirmed: e.target.value || null})}
              disabled={!canEdit()}
            >
              <option value="">⏳ {t('guests.no_response')}</option>
              <option value="accepted">✅ {t('guests.accepted')}</option>
              <option value="maybe">❓ {t('guests.maybe')}</option>
              <option value="declined">❌ {t('guests.declined')}</option>
            </select>
          </div>

          <div className="col-4 col-md-1">
            <label className="form-label small text-muted mb-1">🧑 {t('guests.adults_short')}</label>
            <input
              className="form-control"
              type="number"
              min="1"
              value={newGuest.adults}
              onChange={e => setNewGuest({...newGuest, adults: e.target.value})}
              disabled={!canEdit()}
            />
          </div>

          <div className="col-4 col-md-1">
            <label className="form-label small text-muted mb-1">🧒 {t('guests.children_short')}</label>
            <input
              className="form-control"
              type="number"
              min="0"
              value={newGuest.children}
              onChange={e => setNewGuest({...newGuest, children: e.target.value})}
              disabled={!canEdit()}
            />
          </div>

          <div className="col-4 col-md-1">
            <label className="form-label small text-muted mb-1">💶 {t('guests.gift_value')}</label>
            <input
              className="form-control"
              type="number"
              placeholder="0"
              value={newGuest.giftValue}
              onChange={e => setNewGuest({...newGuest, giftValue: e.target.value})}
              disabled={!canEdit()}
            />
          </div>

          <div className="col-12 col-md-1">
            <button className="btn btn-success w-100" onClick={addGuest} disabled={!canEdit() || saving}>
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder={t('guests.search_placeholder')}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </div>

        {/* Guest table */}
        <div>

          {/* ── Mobile: compact 6-column (hidden on md+) ── */}
          <div className="d-md-none">
            <table className="table table-hover table-sm mb-0 w-100" style={{ fontSize: '0.72rem' }}>
              <thead className="table-success">
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>{t('guests.name')}{sortIcon('name')}</th>
                  <th className="text-center" style={{ width: '32px', cursor: 'pointer', padding: '4px 2px' }} onClick={() => handleSort('invited')}>📨</th>
                  <th className="text-center" style={{ width: '90px', cursor: 'pointer', padding: '4px 2px' }} onClick={() => handleSort('confirmed')}>{t('guests.confirmed_short')}{sortIcon('confirmed')}</th>
                  <th className="text-center" style={{ width: '44px', cursor: 'pointer', padding: '4px 2px' }} onClick={() => handleSort('adults')}>🧑+🧒</th>
                  <th className="text-center" style={{ width: '60px', cursor: 'pointer', padding: '4px 2px' }} onClick={() => handleSort('giftValue')}>🎁€</th>
                  <th className="text-center" style={{ width: '52px', padding: '4px 2px' }}>{t('guests.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredGuests.map(guest => (
                  <tr
                    key={guest.id}
                    className={
                      guest.confirmed === 'accepted' ? 'table-success' :
                      guest.confirmed === 'declined' ? 'table-danger' :
                      guest.confirmed === 'maybe'    ? 'table-warning' : ''
                    }
                  >
                    {editingId === guest.id ? (
                      <>
                        <td style={{ padding: '2px' }}>
                          <input type="text" className="form-control form-control-sm" style={{ fontSize: '0.72rem', padding: '2px 4px' }}
                            value={editData.name} onChange={e => setEditData({...editData, name: toTitleCase(e.target.value)})} autoFocus />
                        </td>
                        <td className="text-center" style={{ padding: '2px' }}>
                          <input type="checkbox" className="form-check-input" checked={editData.invited}
                            onChange={e => setEditData({...editData, invited: e.target.checked})} />
                        </td>
                        <td style={{ padding: '2px' }}>
                          <select className="form-select form-select-sm" style={{ fontSize: '0.7rem', padding: '2px 2px' }}
                            value={editData.confirmed ?? ''} onChange={e => setEditData({...editData, confirmed: e.target.value || null})}>
                            <option value="">⏳</option>
                            <option value="accepted">✅</option>
                            <option value="maybe">❓</option>
                            <option value="declined">❌</option>
                          </select>
                        </td>
                        <td style={{ padding: '2px' }}>
                          <div className="d-flex gap-1">
                            <input type="number" className="form-control form-control-sm" style={{ fontSize: '0.72rem', padding: '2px 2px', width: '36px' }}
                              min="1" value={editData.adults} onChange={e => setEditData({...editData, adults: e.target.value})} />
                            <input type="number" className="form-control form-control-sm" style={{ fontSize: '0.72rem', padding: '2px 2px', width: '36px' }}
                              min="0" value={editData.children} onChange={e => setEditData({...editData, children: e.target.value})} />
                          </div>
                        </td>
                        <td style={{ padding: '2px' }}>
                          <input type="number" className="form-control form-control-sm" style={{ fontSize: '0.72rem', padding: '2px 2px' }}
                            step="0.01" value={editData.giftValue} onChange={e => setEditData({...editData, giftValue: e.target.value})} />
                        </td>
                        <td style={{ padding: '2px', whiteSpace: 'nowrap' }}>
                          <button className="btn btn-sm btn-success me-1" style={{ padding: '1px 5px', fontSize: '0.7rem' }} onClick={saveEdit} disabled={saving}>
                            <i className="fas fa-check"></i>
                          </button>
                          <button className="btn btn-sm btn-secondary" style={{ padding: '1px 5px', fontSize: '0.7rem' }} onClick={cancelEdit}>
                            <i className="fas fa-times"></i>
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="fw-semibold" style={{ padding: '4px 6px' }}>{guest.name}</td>
                        <td className="text-center" style={{ padding: '4px 2px' }}>
                          <input type="checkbox" className="form-check-input" checked={guest.invited}
                            onChange={() => toggleInvited(guest.id, guest.invited)} disabled={!canEdit()} />
                        </td>
                        <td className="text-center" style={{ padding: '2px' }}>
                          <select className="form-select form-select-sm" value={guest.confirmed ?? ''}
                            onChange={e => setConfirmation(guest.id, e.target.value)} disabled={!canEdit()}
                            style={{ fontSize: '0.7rem', padding: '2px 2px' }}>
                            <option value="">⏳ {t('guests.no_response')}</option>
                            <option value="accepted">✅ {t('guests.accepted')}</option>
                            <option value="maybe">❓ {t('guests.maybe')}</option>
                            <option value="declined">❌ {t('guests.declined')}</option>
                          </select>
                        </td>
                        <td className="text-center" style={{ padding: '4px 2px' }}>{guest.adults || 1}+{guest.children || 0}</td>
                        <td className="text-success fw-bold text-center" style={{ padding: '4px 2px' }}>
                          {guest.giftValue ? `€${parseFloat(guest.giftValue).toFixed(0)}` : '-'}
                        </td>
                        <td style={{ padding: '2px', whiteSpace: 'nowrap' }}>
                          <button className="btn btn-sm btn-outline-primary me-1" style={{ padding: '1px 5px', fontSize: '0.7rem' }} onClick={() => startEdit(guest)} disabled={!canEdit()}>
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" style={{ padding: '1px 5px', fontSize: '0.7rem' }} onClick={() => deleteGuest(guest.id)} disabled={!canEdit()}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {filteredGuests.length === 0 && (
                  <tr><td colSpan="6" className="text-muted text-center py-4">
                    {searchText ? t('guests.no_results') : t('guests.no_guests_yet')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Desktop: full 8-column (hidden below md) ── */}
          <div className="d-none d-md-block">
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0">
                <thead className="table-success">
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>{t('guests.name')}{sortIcon('name')}</th>
                    <th className="text-center" style={{ width: '65px', cursor: 'pointer' }} onClick={() => handleSort('invited')}>{t('guests.invited_short')}</th>
                    <th style={{ width: '155px', cursor: 'pointer' }} onClick={() => handleSort('confirmed')}>{t('guests.confirmed_short')}{sortIcon('confirmed')}</th>
                    <th className="text-center" style={{ width: '75px', cursor: 'pointer' }} onClick={() => handleSort('adults')}>{t('guests.adults_short')}</th>
                    <th className="text-center" style={{ width: '85px', cursor: 'pointer' }} onClick={() => handleSort('children')}>{t('guests.children_short')}</th>
                    <th className="text-center" style={{ width: '105px', cursor: 'pointer' }} onClick={() => handleSort('giftValue')}>{t('guests.gift_value')}{sortIcon('giftValue')}</th>
                    <th className="text-center" style={{ width: '85px' }}>{t('guests.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGuests.map(guest => (
                    <tr
                      key={guest.id}
                      className={
                        guest.confirmed === 'accepted' ? 'table-success' :
                        guest.confirmed === 'declined' ? 'table-danger' :
                        guest.confirmed === 'maybe'    ? 'table-warning' : ''
                      }
                    >
                      {editingId === guest.id ? (
                        <>
                          <td>
                            <input type="text" className="form-control form-control-sm" value={editData.name}
                              onChange={e => setEditData({...editData, name: toTitleCase(e.target.value)})} autoFocus />
                          </td>
                          <td className="text-center">
                            <input type="checkbox" className="form-check-input" checked={editData.invited}
                              onChange={e => setEditData({...editData, invited: e.target.checked})} />
                          </td>
                          <td>
                            <select className="form-select form-select-sm" value={editData.confirmed ?? ''}
                              onChange={e => setEditData({...editData, confirmed: e.target.value || null})}>
                              <option value="">⏳ {t('guests.no_response')}</option>
                              <option value="accepted">✅ {t('guests.accepted')}</option>
                              <option value="maybe">❓ {t('guests.maybe')}</option>
                              <option value="declined">❌ {t('guests.declined')}</option>
                            </select>
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm" min="1"
                              value={editData.adults} onChange={e => setEditData({...editData, adults: e.target.value})} />
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm" min="0"
                              value={editData.children} onChange={e => setEditData({...editData, children: e.target.value})} />
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm" step="0.01"
                              value={editData.giftValue} onChange={e => setEditData({...editData, giftValue: e.target.value})} />
                          </td>
                          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                            <button className="btn btn-sm btn-success me-1" onClick={saveEdit} disabled={saving}>
                              <i className="fas fa-check"></i>
                            </button>
                            <button className="btn btn-sm btn-secondary" onClick={cancelEdit}>
                              <i className="fas fa-times"></i>
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="fw-semibold">{guest.name}</td>
                          <td className="text-center">
                            <input type="checkbox" className="form-check-input" checked={guest.invited}
                              onChange={() => toggleInvited(guest.id, guest.invited)} disabled={!canEdit()} />
                          </td>
                          <td>
                            <select className="form-select form-select-sm" value={guest.confirmed ?? ''}
                              onChange={e => setConfirmation(guest.id, e.target.value)} disabled={!canEdit()}>
                              <option value="">⏳ {t('guests.no_response')}</option>
                              <option value="accepted">✅ {t('guests.accepted')}</option>
                              <option value="maybe">❓ {t('guests.maybe')}</option>
                              <option value="declined">❌ {t('guests.declined')}</option>
                            </select>
                          </td>
                          <td className="text-center">{guest.adults || 1}</td>
                          <td className="text-center">{guest.children || 0}</td>
                          <td className="text-success fw-bold text-center">
                            {guest.giftValue ? `€${parseFloat(guest.giftValue).toFixed(2)}` : '-'}
                          </td>
                          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                            <button className="btn btn-sm btn-outline-primary me-1" onClick={() => startEdit(guest)} disabled={!canEdit()}>
                              <i className="fas fa-edit"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => deleteGuest(guest.id)} disabled={!canEdit()}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  {filteredGuests.length === 0 && (
                    <tr><td colSpan="7" className="text-muted text-center py-4">
                      {searchText ? t('guests.no_results') : t('guests.no_guests_yet')}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>

        {/* Statistics */}
        <div className="border-top pt-3 mt-3">
          {/* Row 1: Totals */}
          <div className="row g-3">
            <div className="col-6 col-md-3">
              <div className="bg-primary bg-opacity-10 rounded p-3 text-center">
                <div className="small text-muted">{t('guests.total_guests')}</div>
                <div className="fs-2 fw-bold text-primary">{totalGuests}</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="bg-info bg-opacity-10 rounded p-3 text-center">
                <div className="small text-muted">{t('guests.adults')}</div>
                <div className="fs-2 fw-bold text-info">{totalAdults}</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="bg-warning bg-opacity-10 rounded p-3 text-center">
                <div className="small text-muted">{t('guests.children')}</div>
                <div className="fs-2 fw-bold text-warning">{totalChildren}</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="bg-secondary bg-opacity-10 rounded p-3 text-center">
                <div className="small text-muted">{t('guests.total_persons')}</div>
                <div className="fs-2 fw-bold text-secondary">{totalPersons}</div>
              </div>
            </div>
          </div>

          {/* Row 2: Confirmation breakdown with person counts */}
          <div className="row g-3 mt-2">
            <div className="col-6 col-md-3">
              <div className="bg-primary bg-opacity-10 rounded p-3 text-center">
                <div className="small text-muted">{t('guests.invited')}</div>
                <div className="fs-4 fw-bold text-primary">{invitedCount} / {totalGuests}</div>
                <div className="small">{totalGuests > 0 ? Math.round((invitedCount / totalGuests) * 100) : 0}%</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="bg-success bg-opacity-25 rounded p-3 text-center">
                <div className="small text-success fw-bold">✅ {t('guests.accepted')}</div>
                <div className="fs-4 fw-bold text-success">{acceptedCount}</div>
                <div className="small fw-semibold">{acceptedPersons.total} {t('guests.total_persons').toLowerCase()}</div>
                <div className="small text-muted">{acceptedPersons.adults} {t('guests.adults_short')} · {acceptedPersons.children} {t('guests.children_short')}</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="bg-warning bg-opacity-25 rounded p-3 text-center">
                <div className="small text-warning fw-bold">❓ {t('guests.maybe')}</div>
                <div className="fs-4 fw-bold text-warning">{maybeCount}</div>
                <div className="small fw-semibold">{maybePersons.total} {t('guests.total_persons').toLowerCase()}</div>
                <div className="small text-muted">{maybePersons.adults} {t('guests.adults_short')} · {maybePersons.children} {t('guests.children_short')}</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="bg-danger bg-opacity-10 rounded p-3 text-center">
                <div className="small text-danger fw-bold">❌ {t('guests.declined')}</div>
                <div className="fs-4 fw-bold text-danger">{declinedCount}</div>
                <div className="small fw-semibold">{declinedPersons.total} {t('guests.total_persons').toLowerCase()}</div>
                <div className="small text-muted">{declinedPersons.adults} {t('guests.adults_short')} · {declinedPersons.children} {t('guests.children_short')}</div>
              </div>
            </div>
          </div>

          {/* Row 3: No response + gifts + gift total */}
          <div className="row g-3 mt-2">
            <div className="col-12 col-sm-6 col-md-4">
              <div className="bg-secondary bg-opacity-10 rounded p-3 text-center">
                <div className="small text-muted">⏳ {t('guests.awaiting_confirmation')}</div>
                <div className="fs-4 fw-bold text-secondary">{noResponseCount}</div>
                <div className="small fw-semibold">{noResponsePersons.total} {t('guests.total_persons').toLowerCase()}</div>
                <div className="small">{invitedCount > 0 ? Math.round((noResponseCount / invitedCount) * 100) : 0}% {t('guests.of_invited')}</div>
              </div>
            </div>
            <div className="col-12 col-sm-6 col-md-4">
              <div className="bg-info bg-opacity-10 rounded p-3 text-center">
                <div className="small text-muted">{t('guests.with_gift')}</div>
                <div className="fs-4 fw-bold text-info">{giftCount} / {totalGuests}</div>
                <div className="small">{totalGuests > 0 ? Math.round((giftCount / totalGuests) * 100) : 0}%</div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="bg-success bg-opacity-25 rounded p-3 text-center">
                <div className="small text-success fw-bold">{t('guests.total_gifts_income')}</div>
                <div className="fs-2 fw-bold text-success">€{totalGifts.toFixed(2)}</div>
                <div className="small">{t('guests.average_value')}: €{totalGuests > 0 ? (totalGifts / totalGuests).toFixed(2) : '0.00'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }} onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pb-0">
                <div className="text-center w-100">
                  <div className="fs-1 text-danger mb-2"><i className="fas fa-trash-alt"></i></div>
                  <h4 className="modal-title fw-bold text-danger">{t('guests.delete_guest_title')}</h4>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowDeleteConfirm(false)}></button>
              </div>
              <div className="modal-body pt-3 text-center">
                <p className="text-muted mb-0">{t('guests.delete_guest_confirm')}</p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowDeleteConfirm(false)}>
                  <i className="fas fa-times me-1"></i>{t('common.cancel')}
                </button>
                <button type="button" className="btn btn-danger rounded-pill px-4" onClick={confirmDeleteGuest} disabled={saving}>
                  <i className="fas fa-trash-alt me-1"></i>{saving ? t('guests.deleting') : t('guests.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}